/**
 * Plays Orbiee's streamed voice replies (backend `audio_start` / binary
 * chunks / `audio_end` frames on the shared chat WebSocket).
 *
 * Wire format per chunk:
 *   [ 4 bytes big-endian stream_id ][ mulaw mono audio bytes ]
 *
 * API:
 *   player.unlock()            call on a user gesture (e.g. mic tap)
 *   player.start(data)         on an "audio_start" frame
 *   player.onChunk(buffer)     on a binary WebSocket message
 *   player.end(data)           on an "audio_end" frame
 *   player.stop()              barge-in / stop button
 *   player.destroy()           on disconnect or unmount
 *   player.subscribe(cb)       cb("playing" | "idle") — returns an unsubscribe fn
 *   player.isActive()          true while audio is playing or scheduled
 *
 * "Delivered" is not "heard": the server streams faster than real time, so
 * `audio_end` usually arrives while the shopper is still listening. Idle is
 * therefore reported from the scheduling cursor, not from the frame.
 *
 * Nor does one stream draining mean the turn is done speaking. A turn sends up
 * to two streams (`preamble` then `reply`). The reply can arrive after the
 * preamble has gone quiet, and the preamble often ends with `reason:
 * "superseded"` rather than `"complete"`. Idle is withheld across that gap.
 * Treat "idle" as "the assistant has stopped talking" — what mic hand-back needs.
 */

/** Emitted once all scheduled audio has actually finished playing. */
export const PLAYBACK_IDLE = "idle";
/** Emitted when a stream starts (or a queued stream continues playback). */
export const PLAYBACK_PLAYING = "playing";

const DEFAULTS = {
  prebufferSeconds: 0.2,
  fadeSeconds: 0.04,
  gapSeconds: 0.2,
  /**
   * A turn sends up to two streams: `preamble` then `reply`. The reply's
   * `audio_start` can land *after* the preamble has finished sounding (or
   * after the preamble was faded on a `superseded`/`stopped`/`failed` end),
   * so a drained/ended preamble is not the end of the turn's audio. Keep
   * reporting "playing" for this long after a preamble ends, so callers
   * waiting for the assistant to stop speaking do not act on the gap between
   * the two streams. The reply's `audio_start` cancels this early, so normal
   * turns are unaffected; only the interim→reply gap is bridged.
   *
   * Sized to cover a slow tool call / LLM / synthesis before the reply audio
   * begins. Too short and the mic is handed back mid-turn and the reply then
   * streams into an open mic.
   */
  replyGraceSeconds: 10,
  /**
   * Safety net: if a stream never receives `audio_end` (backend crash,
   * dropped connection mid-stream), report idle anyway so callers waiting
   * on playback — e.g. the voice-mode mic hand-back — cannot deadlock.
   */
  streamWatchdogSeconds: 300,
};

export function createAudioPlayer(options = {}) {
  const PREBUFFER = options.prebufferSeconds ?? DEFAULTS.prebufferSeconds;
  const FADE = options.fadeSeconds ?? DEFAULTS.fadeSeconds;
  const GAP = options.gapSeconds ?? DEFAULTS.gapSeconds;
  const REPLY_GRACE = options.replyGraceSeconds ?? DEFAULTS.replyGraceSeconds;
  const WATCHDOG =
    options.streamWatchdogSeconds ?? DEFAULTS.streamWatchdogSeconds;

  // mulaw byte -> float sample. Built once; this runs sample_rate times per
  // second of speech, so it must be a table, not arithmetic.
  const MULAW = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const u = ~i & 0xff;
    let m = ((u & 0x0f) << 3) + 0x84;
    m <<= (u & 0x70) >> 4;
    const s = u & 0x80 ? 0x84 - m : m - 0x84;
    MULAW[i] = Math.max(-1, Math.min(1, s / 32768));
  }

  let ctx = null;
  let gain = null; // one node at a time, so stop() always works
  let sources = [];
  let streamId = null;
  let streamKind = null; // "preamble" | "reply" — decides the reply grace
  let sampleRate = 16000;
  let cursor = 0; // when the next chunk should start playing
  let lastReason = null; // drives queue-vs-fade on the next stream
  // True while a preamble has drained but its reply has not arrived yet. The
  // turn's audio is not over, so we must keep reporting "playing".
  let awaitingReply = false;
  let underruns = 0;

  const listeners = new Set();
  let state = PLAYBACK_IDLE;
  let idleTimer = null;
  let watchdogTimer = null;

  const emit = (next) => {
    if (state === next) return;
    state = next;
    [...listeners].forEach((cb) => {
      try {
        cb(next);
      } catch (err) {
        console.error("[orbieeAudioPlayer] listener threw", err);
      }
    });
  };

  const clearIdleTimer = () => {
    if (idleTimer != null) {
      clearTimeout(idleTimer);
      idleTimer = null;
    }
  };

  const clearWatchdog = () => {
    if (watchdogTimer != null) {
      clearTimeout(watchdogTimer);
      watchdogTimer = null;
    }
  };

  /** Report idle after `seconds` of real time, unless playback resumes first. */
  const scheduleIdle = (seconds) => {
    clearIdleTimer();
    const ms = Math.max(0, seconds) * 1000 + 80;
    idleTimer = setTimeout(() => {
      idleTimer = null;
      awaitingReply = false;
      clearWatchdog();
      emit(PLAYBACK_IDLE);
    }, ms);
  };

  const unlock = () => {
    if (!ctx) {
      const Ctor = window.AudioContext || window.webkitAudioContext;
      ctx = new Ctor();
    }
    if (ctx.state === "suspended") {
      // Best effort: resume() rejects when called outside a user gesture.
      ctx.resume().catch(() => {});
    }
    return ctx;
  };

  const fadeOut = () => {
    if (!gain || !ctx) {
      sources = [];
      gain = null;
      return;
    }
    const g = gain;
    const dying = sources;
    const endAt = ctx.currentTime + FADE;
    try {
      g.gain.cancelScheduledValues(ctx.currentTime);
      g.gain.setValueAtTime(g.gain.value, ctx.currentTime);
      g.gain.linearRampToValueAtTime(0.0001, endAt);
    } catch {
      /* context closing */
    }
    dying.forEach((s) => {
      try {
        s.stop(endAt);
      } catch {
        /* already stopped */
      }
    });
    setTimeout(() => {
      try {
        g.disconnect();
      } catch {
        /* already disconnected */
      }
    }, FADE * 1000 + 60);
    sources = [];
    gain = null;
  };

  const start = (data = {}) => {
    const c = unlock();

    // A stream that ended cleanly may still be audible (the server streams
    // ahead of real time), so queue behind it — this is the
    // preamble -> reply case. Anything else is a barge-in: cut it.
    const queue = lastReason === "complete" && cursor > c.currentTime;
    const resumeAt = cursor;
    if (!queue) fadeOut();

    streamId = data.stream_id;
    // Absent `kind` is treated as a reply: that grants no grace, which keeps
    // hand-back as prompt as it was before rather than delaying every turn.
    streamKind = data.kind || "reply";
    awaitingReply = false;
    sampleRate = data.sample_rate || 16000;
    underruns = 0;
    cursor = queue
      ? Math.max(resumeAt + GAP, c.currentTime + 0.02)
      : c.currentTime + PREBUFFER;

    if (!gain) {
      gain = c.createGain();
      gain.gain.setValueAtTime(0.0001, c.currentTime);
      gain.gain.linearRampToValueAtTime(1, c.currentTime + 0.02);
      gain.connect(c.destination);
    }

    clearIdleTimer();
    clearWatchdog();
    watchdogTimer = setTimeout(() => {
      watchdogTimer = null;
      // Abandon the stream, don't just report idle: leaving `streamId` set
      // would keep isActive() true forever and deadlock the mic hand-back.
      streamId = null;
      streamKind = null;
      awaitingReply = false;
      emit(PLAYBACK_IDLE);
    }, WATCHDOG * 1000);
    emit(PLAYBACK_PLAYING);
  };

  const onChunk = (buf) => {
    if (streamId === null || streamId === undefined || !ctx || !gain) return;
    if (!buf || buf.byteLength <= 4) return;
    const view = new DataView(buf);
    if (view.getUint32(0, false) !== streamId) return; // stale chunk

    const raw = new Uint8Array(buf, 4);
    const pcm = new Float32Array(raw.length);
    for (let i = 0; i < raw.length; i++) pcm[i] = MULAW[raw[i]];

    const audioBuf = ctx.createBuffer(1, pcm.length, sampleRate);
    audioBuf.getChannelData(0).set(pcm);
    const src = ctx.createBufferSource();
    src.buffer = audioBuf;
    src.connect(gain);

    // Never schedule in the past — it plays immediately and overlaps.
    if (cursor < ctx.currentTime) {
      cursor = ctx.currentTime + 0.02;
      underruns++;
    }
    src.start(cursor);
    cursor += audioBuf.duration;
    sources.push(src);
    if (sources.length > 400) sources = sources.slice(-200);
  };

  const end = (data = {}) => {
    if (data.stream_id !== streamId) return;
    lastReason = data.reason || "complete";
    const endedKind = streamKind;
    streamId = null;
    streamKind = null;
    clearWatchdog();

    // `superseded` = a newer stream is taking over (interim → real reply, or
    // an updated reply). Fade the old audio, but do NOT report idle — that is
    // what was reopening the mic while the next stream was still about to
    // start / play. Stay "playing" until the next audio_start (or grace).
    if (lastReason === "superseded") {
      fadeOut();
      awaitingReply = true;
      scheduleIdle(REPLY_GRACE);
      return;
    }

    // `stopped` / `failed`. A preamble is always followed by the reply, so
    // even when the interim ends this way the turn's audio is not over — keep
    // reporting "playing" across the gap until the reply's audio_start (which
    // cancels this) or the bridge window expires. Reopening the mic here is
    // exactly what let the reply stream into an open mic. A `reply` (or
    // unlabelled) stream ending this way is a genuine stop/failure — hand back
    // promptly as before.
    if (lastReason !== "complete") {
      fadeOut();
      if (endedKind === "preamble") {
        awaitingReply = true;
        scheduleIdle(REPLY_GRACE);
      } else {
        awaitingReply = false;
        scheduleIdle(FADE);
      }
      return;
    }

    // Wait for the audio already scheduled ahead of the clock to play out.
    const remaining = ctx ? cursor - ctx.currentTime : 0;
    // A preamble is always followed by the reply, whose `audio_start` may not
    // arrive until after the preamble has drained. Stay "playing" across that
    // gap so the mic is not handed back mid-turn.
    const grace = endedKind === "preamble" ? REPLY_GRACE : 0;
    awaitingReply = grace > 0;
    scheduleIdle(remaining + grace);
  };

  const stop = () => {
    fadeOut();
    streamId = null;
    streamKind = null;
    lastReason = null;
    awaitingReply = false;
    cursor = 0;
    clearWatchdog();
    scheduleIdle(FADE);
  };

  const destroy = () => {
    stop();
    clearIdleTimer();
    clearWatchdog();
    state = PLAYBACK_IDLE;
    listeners.clear();
    if (ctx) {
      try {
        ctx.close();
      } catch {
        /* already closed */
      }
      ctx = null;
    }
  };

  const subscribe = (cb) => {
    if (typeof cb !== "function") return () => {};
    listeners.add(cb);
    return () => listeners.delete(cb);
  };

  /**
   * True while a stream is open, audio is still scheduled to play, or a
   * preamble's reply is still expected.
   */
  const isActive = () => {
    if (streamId !== null && streamId !== undefined) return true;
    if (awaitingReply) return true;
    return !!ctx && cursor > ctx.currentTime;
  };

  return {
    unlock,
    start,
    onChunk,
    end,
    stop,
    destroy,
    subscribe,
    isActive,
    get underruns() {
      return underruns;
    },
  };
}

export default createAudioPlayer;
